import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { ChatText } from '@phosphor-icons/react';
import { LazyTooltip } from '../../../design-system/Tooltip';
import { Icon } from '../../../design-system/icons/Icon';
import { LastSeenSelector } from '../../../components/LastSeenSelector';
import CustomStats from '../../../seo_components/CustomStats';
import { useViewMode, type ViewMode } from '../hooks/useViewMode';
import type { IntlShape } from 'react-intl';
import type { SrpStaticStrings, LastSeenOptionValue } from '../../../features/srp/SrpViewModel';
import {
  SRP_INLINE_SELECT_CLASSNAME,
  SRP_INLINE_SELECT_STATIC_CLASSNAME,
} from '../../../utils/srp/srp-inline-select-classname';
import { trackEvent } from '../../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';
import {
  MapViewModeToggle,
  VIEW_TOGGLE_GROUP_CLASS,
  VIEW_TOGGLE_BTN_BASE,
  VIEW_TOGGLE_BTN_ACTIVE,
  VIEW_TOGGLE_BTN_INACTIVE,
} from '../../../components/map/MapViewModeToggle';

export interface SearchSidebarProps {
  intl?: IntlShape;
  /** Inspector-aware formatMessage, required alongside `intl` whenever `staticStrings` isn't provided. */
  formatMessage?: ReturnType<typeof useFormatMessage>;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentRefinement: string;
  handleSortChange: (value: string) => void;
  hasActiveSearch: boolean;
  location: string | null;
  /** When set, company header is shown; hide duplicate stats row (e.g. "64 jobs at MongoDB"). */
  companyId?: string | null;
  /** Set when SearchHeader is rendering a hub header with its own <h1> (role, work-model, etc.);
   * hides the stats row's <h1> so the page doesn't ship two. */
  hasPageLevelHeading?: boolean;
  className?: string;
  /** When provided (e.g. SRP static/island without SearchProvider), show simple job count instead of CustomStats. @deprecated Prefer staticStatsText + staticShowFinlandSubtitle for stable CustomStats display. */
  staticNbHits?: number;
  /** Pre-computed stats headline for CustomStats (static SRP); keeps CustomStats stable, only the string changes. */
  staticStatsText?: string;
  /** Whether to show Finland jobs subtitle when using staticStatsText. */
  staticShowFinlandSubtitle?: boolean;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided, intl is not used. */
  staticStrings?: SrpStaticStrings;
  /** Resolved Last Seen option for static sidebar (from view model). */
  initialLastSeenOption?: LastSeenOptionValue;
  /** When provided, show feedback button on the stats row (right-aligned). */
  feedbackModalOpen?: boolean;
  setFeedbackModalOpen?: (open: boolean) => void;
  /**
   * Navbar live-index redesign, Phase 2 fix P1: when true, keep rendering the stats row exactly
   * as today (same element, same text, same position: this is where the SRP's <h1> lives, see
   * `hasPageLevelHeading` above) but drop the controls row (results count's sibling controls:
   * view toggle, sort select, last-seen select, feedback button). Those controls move into the
   * header's own tape/strip and the Advanced Filters modal when the flag is on. Do NOT fork the
   * heading logic; only gate the second row.
   */
  liveIndex?: boolean;
}

/**
 * Search sidebar component - contains stats, view mode toggle, and sort / recency controls.
 * When staticStrings is provided, renders without useIntl (for static SRP).
 */
export const SearchSidebar: React.FC<SearchSidebarProps> = (props) => {
  if (props.staticStrings) {
    return <SearchSidebarWithStaticStrings {...props} />;
  }
  return <SearchSidebarWithIntl {...props} />;
};

const SEGMENT_GROUP_CLASS =
  'rounded-xl bg-[#F2EADF] dark:bg-[#2A1D0F] p-1 border border-[#DDD5C8] dark:border-[#4A3520]';

const SEGMENT_BTN_ACTIVE =
  'min-h-11 bg-white dark:bg-[#1F1509] text-[#3B5E45] dark:text-[#98CFA8] shadow dark:shadow-none border border-[#3B5E45] dark:border-[#98CFA8]';

const SEGMENT_BTN_INACTIVE =
  'min-h-11 bg-transparent text-[#3D3529] dark:text-[#C4A882] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] border border-transparent';

// VIEW_TOGGLE_GROUP_CLASS / VIEW_TOGGLE_BTN_* (used by the static-strings variant's inline toggle
// below) now live in components/map/MapViewModeToggle.tsx, alongside the extracted intl-variant
// toggle component — see that file's doc comment for why it was pulled out of here.

function SearchSidebarWithStaticStrings({
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  location,
  companyId = null,
  hasPageLevelHeading = false,
  className = '',
  staticStatsText,
  staticShowFinlandSubtitle,
  staticStrings,
  initialLastSeenOption = 'all',
  liveIndex = false,
}: SearchSidebarProps & { staticStrings: SrpStaticStrings; initialLastSeenOption?: LastSeenOptionValue }) {
  const hideStatsRow = location || companyId || hasPageLevelHeading;
  const isStaticSidebar = true;
  const sortMessages = staticStrings.sortLabels;
  const lastSeenValue = initialLastSeenOption ?? 'all';
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) return refinement.replace('_postedDate_desc', '');
    if (refinement.endsWith('_salaryRangeMin_desc')) return refinement.replace('_salaryRangeMin_desc', '');
    return refinement;
  };
  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);
  const handleMapToggle = () => {
    trackEvent({ name: 'srp_map_interaction', params: { action: 'toggle_to_map' } });
    setViewMode('map');
  };
  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];

  return (
    <aside
      id="search-controls"
      className={`search-controls text-[#1E1A14] dark:text-[#F2EADF] px-2 @sm:px-2 @md:px-4 pb-0 flex flex-col ${hasActiveSearch ? '' : '@[900px]:flex-row @[900px]:items-center @[900px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={staticStrings.controlsNavigation}
    >
      {/* Stats row: job count + inline "new this week" + Finland tooltip (hidden when location or
          company header is shown, and on mobile in map mode — see SearchSidebarWithIntl's version
          of this comment for why). */}
      <div className={`${viewMode === 'map' ? 'hidden lg:flex' : 'flex'} items-start @[900px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow && staticStatsText !== undefined && (
            <CustomStats
              statsText={staticStatsText}
              showFinlandSubtitle={staticShowFinlandSubtitle}
              finlandSubtitle={staticStrings.statsFinlandSubtitle}
              recentJobsSubtitle={staticStrings.recentJobsSubtitleFormatted}
            />
          )}
          {!hideStatsRow && staticStatsText === undefined && (
            <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">{staticStrings.jobCountFormatted}</p>
          )}
        </div>
      </div>
      {!liveIndex && (
      <div
        className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto ${
          viewMode === 'map' ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <div className={VIEW_TOGGLE_GROUP_CLASS}>
          {/* Tooltip describes the switch-to action only while inactive: while a view is already
              showing, its own button's tooltip just names it instead of instructing you to
              "switch to" what's already on screen. */}
          <LazyTooltip content={viewMode === 'list' ? staticStrings.viewList : staticStrings.tooltipViewList} position="bottom">
            <button type="button" className={`${VIEW_TOGGLE_BTN_BASE} ${viewMode === 'list' ? VIEW_TOGGLE_BTN_ACTIVE : VIEW_TOGGLE_BTN_INACTIVE}`} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label={staticStrings.viewList}>
              <Icon name="bars4" useMaskForColor className="h-4 w-4" />
            </button>
          </LazyTooltip>
          <LazyTooltip content={viewMode === 'map' ? staticStrings.viewMap : staticStrings.tooltipViewMap} position="bottom">
            <button type="button" className={`${VIEW_TOGGLE_BTN_BASE} ${viewMode === 'map' ? VIEW_TOGGLE_BTN_ACTIVE : VIEW_TOGGLE_BTN_INACTIVE}`} onClick={handleMapToggle} aria-pressed={viewMode === 'map'} aria-label={staticStrings.viewMap}>
              <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
            </button>
          </LazyTooltip>
        </div>
        {/* Right cluster: sort controls + feedback placeholder, grouped so the parent's justify-between still only sees two flex items.
            Hidden on mobile in map mode, see SearchSidebarWithIntl's version of this comment for why. */}
        <div className={`${viewMode === 'map' ? 'hidden lg:flex' : 'flex'} items-center gap-2 @sm:gap-2 @md:gap-3`}>
          <div className="flex items-center gap-1 @sm:gap-2 @[1600px]:hidden">
            <select className={SRP_INLINE_SELECT_CLASSNAME} value={currentRefinement} onChange={(e) => handleSortChange(e.target.value)} aria-label={staticStrings.sortAriaLabel}>
              <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
              <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
              <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
            </select>
            <select className={SRP_INLINE_SELECT_STATIC_CLASSNAME} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
              {staticStrings.lastSeenOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="hidden min-w-0 items-center gap-2 @[1600px]:flex">
            <div className={`flex ${SEGMENT_GROUP_CLASS}`}>
              {sortSegments.map((seg) => (
                <button key={seg.value} type="button" className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:z-10 cursor-pointer ${currentRefinement === seg.value ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => handleSortChange(seg.value)} aria-pressed={currentRefinement === seg.value}>
                  {seg.label}
                </button>
              ))}
            </div>
            <div className="min-w-0 max-w-[min(100%,14rem)] shrink-0">
              <select className={SRP_INLINE_SELECT_STATIC_CLASSNAME} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
                {staticStrings.lastSeenOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          {!hideStatsRow && (
            <span
              className="hidden @[900px]:inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-[#3D3529] dark:text-[#C4A882] bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] rounded-lg shadow-sm dark:shadow-none cursor-default"
              aria-hidden
            >
              <ChatText className="w-4 h-4" />
              {staticStrings.feedbackButtonLabel}
            </span>
          )}
        </div>
      </div>
      )}
    </aside>
  );
}

function SearchSidebarWithIntl({
  intl,
  formatMessage,
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  location,
  companyId = null,
  hasPageLevelHeading = false,
  className = '',
  staticNbHits,
  staticStatsText,
  staticShowFinlandSubtitle,
  feedbackModalOpen,
  setFeedbackModalOpen,
  liveIndex = false,
}: SearchSidebarProps) {
  // Real callers on this (non-staticStrings) path always pass both, this narrows them from
  // optional (shared with SearchSidebarWithStaticStrings, which needs neither) to non-optional
  // for the rest of the function, so call sites below stay plain `formatMessage(...)` rather than
  // `formatMessage(...)`: the non-null assertion operator wraps the whole callee in a
  // TSNonNullExpression, which formatjs's extractor doesn't recognize as a bare `formatMessage`
  // call (unlike `intl!.formatMessage(...)`, where `!` only wraps the object, not the callee).
  if (!intl || !formatMessage) return null;
  const isStaticSidebar = staticStatsText !== undefined || staticNbHits != null;
  const hideStatsRow = location || companyId || hasPageLevelHeading;
  const handleMapToggle = () => {
    trackEvent({ name: 'srp_map_interaction', params: { action: 'toggle_to_map', locale: intl.locale } });
    setViewMode('map');
  };

  const sortMessages = {
    relevance: formatMessage({
      id: 'sort.relevance',
      defaultMessage: 'Relevance',
      description: 'Sort option label to sort jobs by relevance to search query',
    }),
    newest: formatMessage({
      id: 'sort.newest',
      defaultMessage: 'Newest',
      description: 'Sort option label to sort jobs by newest first',
    }),
    salary: formatMessage({
      id: 'sort.salary',
      defaultMessage: 'Salary',
      description: 'Sort option label to sort jobs by salary (highest first)',
    }),
  };

  // Extract the base index prefix from currentRefinement
  // currentRefinement can be: "en_search", "en_search_postedDate_desc", "en_search_salaryRangeMin_desc", etc.
  // We need to remove the sort suffix if present
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) {
      return refinement.replace('_postedDate_desc', '');
    }
    if (refinement.endsWith('_salaryRangeMin_desc')) {
      return refinement.replace('_salaryRangeMin_desc', '');
    }
    return refinement;
  };

  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);

  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];

  const feedbackButton = !hideStatsRow && setFeedbackModalOpen != null && (
    <button
      type="button"
      onClick={() => setFeedbackModalOpen(true)}
      className="hidden @[900px]:inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-[#3D3529] dark:text-[#C4A882] bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] rounded-lg hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-colors shadow-sm dark:shadow-none cursor-pointer"
    >
      <ChatText className="w-4 h-4" />
      <Msg
        id="feedback.button.label"
        defaultMessage="Give us feedback"
        description="Button label to open feedback modal"
      />
    </button>
  );

  return (
    <aside
      id="search-controls"
      className={`search-controls text-[#1E1A14] dark:text-[#F2EADF] px-2 @sm:px-2 @md:px-4 pb-0 flex flex-col ${hasActiveSearch ? '' : '@[900px]:flex-row @[900px]:items-center @[900px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={formatMessage({
        id: 'search.controls.navigation',
        defaultMessage: 'Search controls and filters',
        description: 'Accessibility label for search controls and filter section',
      })}
    >
      {/* Stats row: job count + inline "new this week" + Finland tooltip (hidden when location or
          company header is shown). Also hidden on mobile in map mode — the map's own panel
          already surfaces a live job count there, and this row was pushing the (already
          vertical-space-constrained, near-fullscreen) map down toward the bottom-nav-covered
          edge of the screen. Desktop keeps it: the map is a side-by-side split there, not
          fullscreen, so there's no similar space pressure. */}
      <div className={`${viewMode === 'map' ? 'hidden lg:flex' : 'flex'} items-start @[900px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow
            ? (staticStatsText !== undefined
                ? (
                    <CustomStats
                      statsText={staticStatsText}
                      showFinlandSubtitle={staticShowFinlandSubtitle}
                    />
                  )
                : staticNbHits != null
                  ? (
                      <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                        <Msg
                          id="search.jobCount"
                          defaultMessage="{count, number} jobs"
                          description="Number of job results"
                          values={{ count: staticNbHits }}
                        />
                      </p>
                    )
                  : <CustomStats />)
            : null}
        </div>
      </div>

      {/* Main content row - contains selectors and buttons. On mobile in map mode this whole row
          hides: the view toggle itself now floats over the map instead (see MapView.tsx's
          `mapOverlay` prop / SrpView.tsx) so the map can use the reclaimed vertical space, and the
          sort/last-seen cluster isn't duplicated there since sort order doesn't meaningfully
          affect a geographic layout the way it does a list, and recency has its own control on
          the map itself (MapToolsCluster's "new this week" toggle). Desktop (lg+) keeps this row
          visible in both view modes: there's no vertical space pressure there, and it's a
          side-by-side split rather than a fullscreen map. */}
      {!liveIndex && (
      <div
        className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto ${
          viewMode === 'map' ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <MapViewModeToggle
          viewMode={viewMode}
          onSelectList={() => setViewMode('list')}
          onSelectMap={handleMapToggle}
          formatMessage={formatMessage}
        />
        {/* Right cluster: sort controls + feedback, grouped so the parent's justify-between still only sees two flex items.
            Hidden on mobile in map mode along with the rest of this row, see the main-content-row comment above for why. */}
        <div className={`${viewMode === 'map' ? 'hidden lg:flex' : 'flex'} items-center gap-2 @sm:gap-2 @md:gap-3`}>
          {/* iOS-style select for md and down */}
          <div className="flex items-center gap-1 @sm:gap-2 @[1600px]:hidden">
            <select
              className={SRP_INLINE_SELECT_CLASSNAME}
              value={currentRefinement}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label={formatMessage({
                id: 'sort.ariaLabel',
                defaultMessage: 'Sort results',
                description: 'Accessibility label for sort dropdown to change result ordering',
              })}
            >
              <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
              <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
              <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
            </select>
            {!isStaticSidebar && (
              <div className="min-w-0 max-w-[min(100%,14rem)] shrink-0">
                <LastSeenSelector />
              </div>
            )}
          </div>
          {/* iOS-style segmented control for lg and up */}
          <div className="hidden min-w-0 items-center gap-2 @[1600px]:flex">
            <div className={`flex ${SEGMENT_GROUP_CLASS}`}>
              {sortSegments.map((seg) => (
                <button
                  key={seg.value}
                  type="button"
                  className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:z-10 cursor-pointer ${
                    currentRefinement === seg.value ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
                  }`}
                  onClick={() => handleSortChange(seg.value)}
                  aria-pressed={currentRefinement === seg.value}
                >
                  {seg.label}
                </button>
              ))}
            </div>
            {!isStaticSidebar && (
              <div className="hidden min-w-0 max-w-[min(100%,14rem)] shrink-0 @[1600px]:block">
                <LastSeenSelector />
              </div>
            )}
          </div>
          {feedbackButton}
        </div>
      </div>
      )}
    </aside>
  );
};
